/**
 * AttendeeChips primitive (Plan 03-06).
 *
 * Phase 3 D-05: AvatarStack wrapper with calendar-specific aria-label.
 * Wraps Phase 2 <AvatarStack> + <PixelAvatar> for event attendee display.
 *
 * When `colorSlots` is provided (server can resolve member color tokens),
 * renders PixelAvatars in the AvatarStack. When not provided (member lookup
 * not available), renders a compact text fallback showing the attendee count.
 *
 * size:
 *   'sm' = 20px avatars (compact month-cell rows)
 *   'md' = 24px avatars (full/today-strip EventCard variants)
 */
import { useTranslation } from 'react-i18next';
import { AvatarStack } from './AvatarStack';
import type { ColorSlot } from './PixelAvatar';

const MAX_SHOWN = 3;

interface AttendeeChipsProps {
  /** Attendee member IDs — used for aria-label and count display. */
  memberIds: string[];
  /** Avatar size — 'sm' (20px) for compact month cells, 'md' (24px) for full cards. */
  size?: 'sm' | 'md';
  /** Optional display names keyed by member ID — included in aria-label when available. */
  memberNames?: Record<string, string>;
  /** Optional color slots keyed by member ID — when provided, renders PixelAvatars. */
  memberColorSlots?: Record<string, ColorSlot>;
}

export function AttendeeChips({
  memberIds,
  size = 'sm',
  memberNames,
  memberColorSlots,
}: AttendeeChipsProps) {
  const { t } = useTranslation();

  const names = memberIds
    .map((id) => memberNames?.[id])
    .filter((n): n is string => Boolean(n))
    .join(', ');

  const ariaLabel = names
    ? `${t('calendar.field.attendees')}: ${names}`
    : t('calendar.field.attendees');

  const pxSize = size === 'sm' ? 20 : 24;

  // Resolve color slots for the first MAX_SHOWN attendees
  const visibleIds = memberIds.slice(0, MAX_SHOWN);
  const overflowCount = Math.max(0, memberIds.length - MAX_SHOWN);

  const colorSlots = memberColorSlots
    ? visibleIds
        .map((id) => memberColorSlots[id])
        .filter((s): s is ColorSlot => Boolean(s))
    : undefined;

  return (
    <span
      data-testid="attendee-chips"
      aria-label={ariaLabel}
      className="inline-flex items-center gap-1"
    >
      {colorSlots && colorSlots.length > 0 ? (
        <>
          <AvatarStack colorSlots={colorSlots} size={pxSize} />
          {overflowCount > 0 && (
            <span className="text-badge font-medium text-ink-2">+{overflowCount}</span>
          )}
        </>
      ) : memberIds.length > 0 ? (
        /* Fallback when color slots not available — show avatar count chip */
        <span className="inline-flex items-center justify-center w-5 h-5 rounded-pill bg-inset text-ink-2 text-badge font-semibold">
          {memberIds.length}
        </span>
      ) : null}
    </span>
  );
}
